import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'

import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { InvoicesTableModel, ObjectionTableModel, TabsInvoicesType } from '../../invoicesPage.Model'

import {
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableRowsPerPageForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../../redux/helpers'

import {
  AllInbox,
  Assessment,
  Autorenew,
  Block,
  Edit,
  ListOutlined,
  LoupeOutlined,
  PriorityHigh
} from '@material-ui/icons'
import {
  EnumCommodity,
  EnumEstadoCiclo,
  EnumEstadoFactura,
  EnumTipoFactura,
  EnumTipoFacturacion
} from '../../../../api'
import { EnumClientTypes } from '../../../../api/Argentia/enums/enumClientTypes'
import { getUserPermissions, hasPermission } from '../../../../helpers/permissionsHelper'
import { useHttpError } from '../../../../hooks/useHttpError'
import { ActionIcons, DateFilters, DatePeriod, NonAuthRoutes } from '../../../../model'
import { Pages, Resources } from '../../../../model/RBAC/constants'
import {
  getAnnulmentCycleStatus,
  getReinvoiceCycleStatus
} from '../../../../pages/invoiceCycles/invoiceCycles.service'
import { createReinvoiceBatch } from '../../../../pages/settlementBatchs/settlementBatchs.services'
import { DataTableFilter, RootStoreModel } from '../../../../redux'
import {
  filterAnnullableInvoices,
  filterReinvoiceableInvoices,
  isAnnullableInvoice,
  isManualPenaltyInvoice,
  isReinvoiceableInvoice
} from '../../invoicePage.mapper'
import {
  deleteObjection,
  fetchClientDataByCUPS,
  fetchDeliveryNotes,
  fetchInvoiceDetails,
  fetchInvoices,
  fetchObjectionsByInvoiceID,
  fetchObjectionsDescriptions,
  filterApplicableActionsConfiguration,
  generateCancellationConcepts,
  saveObjection
} from '../../invoicesPage.service'

import { enumToSelectOptionArray, enumValueToKey } from '../../../../helpers'

import { Menu, MenuItem, Typography } from '@material-ui/core'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import Warning from '@material-ui/icons/Warning'
import { useDispatch, useSelector } from 'react-redux'
import {
  AnnulmentCycleModel,
  ReinvoiceCycleModel
} from '../../../../api/Argentia/endpoints/invoiceCyclesExecutions/model/annulmentCycleStatus'
import { InvoiceResponseModel } from '../../../../api/Argentia/endpoints/invoices/model/invoices'
import { EjecucionCFProcessingData } from '../../../../api/SignalRPOC/model/hubEventData'
import {
  ExecutionCycleFormDataModel,
  ExecutionCycleFormDialog
} from '../../../../components/shared/executionCycleFormDialog/executionCycleFormDialog'
import { ObjectionsDialog } from '../../../../components/shared/objectionsDialog/objectionsDialog'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'
import { generateIconsFromConfiguration } from '../../../../helpers/dataTableActionIconHelper'
import { apiDownloadFiles } from '../../../../api/Pioneer/endpoints/files/files.api'
import { EnumFileExtension } from '../../../../api/Pioneer/enums/enumFileExtension'
import { EnumFileType } from '../../../../api/Pioneer/enums/enumFileType'
import { mapFileStreamToFile } from '../../../../helpers/fileStreamToFile'
import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { useConfirmationDialog, usePageState } from '../../../../hooks'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { configureConfirmationDialog, openConfirmationDialog } from '../../../../redux'
import { addInvoiceCycleProcess } from '../../../../redux/actions/processing.actions'
import { ClientFileModel } from '../../../clients/clients.model'
import { ProcedureFormMode } from '../../../procedures/procedures.types'

export type InvoicesTableConfig = {
  elevation?: number
  rowsPerPage?: number
  rowsPerPageOptions?: number[]
  queryInvoiceStates?: EnumEstadoFactura[] | null
  hideTableColumns?: string[]
}

const defaultTableConfig: InvoicesTableConfig = {
  elevation: 1,
  rowsPerPage: 10,
  rowsPerPageOptions: undefined,
  queryInvoiceStates: null
}

export interface InvoicesTableProps {
  commodity?: EnumCommodity
  clientType: EnumClientTypes
  activeTabInvoiceType?: TabsInvoicesType
  dateRange: DatePeriod
  activeDateFilter: DateFilters | undefined
  dataTableFilter: DataTableFilter | undefined
  customFilter: any | undefined
  dataTableSearch?: string | undefined
  onFetching?: (isFetching: boolean) => void
  config?: InvoicesTableConfig
  data?: InvoicesTableModel[]
}

export const InvoicesTable: React.FC<InvoicesTableProps> = props => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'invoicesPage',
    'enums',
    'storageHelper',
    'procedures'
  ])

  const dispatch = useDispatch()
  const { info, error, addEventListener, deleteEventListener } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { dataTableRowsPerPage } = usePageState()

  const tableConfiguration = props.config || defaultTableConfig
  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const [invoices, setInvoices] = useState<InvoicesTableModel[] | undefined>(undefined)
  const [isNavigating, setIsNavigating] = useState<boolean>(false)

  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = React.useState(null)
  const [invoiceRow, setInvoiceRow] = useState<InvoicesTableModel | undefined>(undefined)
  const [deliveryNotesData, setDeliveryNotesData] = useState<InvoiceResponseModel[] | undefined>([])
  const [aggregatedInvoiceData, setAggregatedInvoiceData] = useState<
    InvoiceResponseModel[] | undefined
  >([])

  const [objections, setObjections] = useState<ObjectionTableModel[] | undefined>(undefined)

  //const [criticality, setCriticality] = useState<SelectOption[]>([]);
  const [objDescriptions, setObjDescriptions] = useState<ObjectionTableModel[] | undefined>(
    undefined
  )

  const [showObjectionDialog, setShowObjectionDialog] = useState<boolean>(false)
  const [showDeliveryNotesDialog, setShowDeliveryNotesDialog] = useState<boolean>(false)
  const [showAggregatedInvoiceDialog, setShowAggregatedInvoiceDialog] = useState<boolean>(false)

  // States for annulment cycle selection dialog
  const [showAnnulmentCycleDialog, setShowAnnulmentCycleDialog] = useState<boolean>(false)
  const [pendingAnnulmentData, setPendingAnnulmentData] = useState<{
    cycles: AnnulmentCycleModel[]
    minDate: moment.Moment
    maxDate: moment.Moment
    invoiceIds: number[]
    penaltyInvoiceIds: number[]
  } | null>(null)
  const [isSubmittingAnnulment, setIsSubmittingAnnulment] = useState<boolean>(false)

  // stores form data between submit and SignalR delivery of executionCycleId
  const pendingCycleLabelRef = useRef<string | undefined>(undefined)
  const pendingBillingTypeRef = useRef<EnumTipoFacturacion | undefined>(undefined)

  // States for reinvoice cycle selection dialog
  const [showReinvoiceCycleDialog, setShowReinvoiceCycleDialog] = useState<boolean>(false)
  const [pendingReinvoiceData, setPendingReinvoiceData] = useState<{
    cycles: ReinvoiceCycleModel[]
    minDate: moment.Moment
    maxDate: moment.Moment
    invoiceIds: number[]
    penaltyInvoiceIds: number[]
  } | null>(null)
  const [isSubmittingReinvoice, setIsSubmittingReinvoice] = useState<boolean>(false)

  const history = useHistory()
  const { showError, clearError, errorProps } = useHttpError()

  const storageContainer = useSelector((state: RootStoreModel) => {
    return (
      state.profile?.user?.domain &&
      `hd-${state.profile?.user?.domain.substr(0, state.profile?.user?.domain.indexOf('.'))}`
    )
  })

  //Enums Starts

  enum DownloadMode {
    HTML,
    XML,
    XSIG,
    PDF,
    XLSX
  }

  //Enums Ends

  //Form definition Starts
  //Form definitions ends

  // Handlers Starts
  const showCancellationDialog = async (rowsToCancel: any[], recalculatePenalties: boolean) => {
    const numFacturas = rowsToCancel.length
    const plural = numFacturas !== 1 ? 's' : ''

    return new Promise<void>(resolve => {
      openConfirmationDialog()
      configureConfirmationDialog({
        title: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoices.title')
          : t('invoicesPage:dialogs.cancelInvoices.title'),
        message: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoices.message', { count: numFacturas, plural })
          : t('invoicesPage:dialogs.cancelInvoices.message', { count: numFacturas, plural }),
        okBtnLabel: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoices.okBtnLabel')
          : t('invoicesPage:dialogs.cancelInvoices.okBtnLabel'),
        cancelBtnLabel: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoices.cancelBtnLabel')
          : t('invoicesPage:dialogs.cancelInvoices.cancelBtnLabel'),
        onClose: async (confirmed: boolean) => {
          if (confirmed) {
            const invoiceIDs = rowsToCancel.map(row => row.id)
            await generateCancellationConcepts(invoiceIDs, recalculatePenalties).then(response => {
              if (response.success) {
                info(t('invoicesPage:notifications.success.cancelInvoices'))

                fetchInvoicesList({
                  commodity: props.commodity,
                  clientType: props.clientType,
                  activePageTab: props.activeTabInvoiceType,
                  dateRange: props.dateRange
                })
              } else {
                error(t('invoicesPage:notifications.error.cancelInvoices'))
              }
            })
          }
          closeConfirmation()
          resolve()
        }
      })
    })
  }

  const cancelInvoice = async (row: InvoicesTableModel, recalculatePenalties: boolean) => {
    return new Promise<void>(resolve => {
      openConfirmationDialog()
      configureConfirmationDialog({
        title: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoice.title')
          : t('invoicesPage:dialogs.cancelInvoice.title'),
        message: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoice.message', { code: row.code })
          : t('invoicesPage:dialogs.cancelInvoice.message', { code: row.code }),
        okBtnLabel: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoice.okBtnLabel')
          : t('invoicesPage:dialogs.cancelInvoice.okBtnLabel'),
        cancelBtnLabel: recalculatePenalties
          ? t('invoicesPage:dialogs.recalculateInvoice.cancelBtnLabel')
          : t('invoicesPage:dialogs.cancelInvoice.cancelBtnLabel'),
        onClose: async (confirmed: boolean) => {
          if (confirmed) {
            await generateCancellationConcepts([row.id], recalculatePenalties).then(response => {
              if (response.success) {
                info(t('invoicesPage:notifications.success.cancelInvoice'))
                fetchInvoicesList({
                  commodity: props.commodity,
                  clientType: props.clientType,
                  activePageTab: props.activeTabInvoiceType,
                  dateRange: props.dateRange
                })
              } else {
                error(t('invoicesPage:notifications.error.cancelInvoices'))
              }
            })
          }
          closeConfirmation()
          resolve()
        }
      })
    })
  }

  const cancelInvoices = async (rows: any[], recalculatePenalties: boolean) => {
    // se reemplaza por ciclo anulacion
  }

  const processPenaltyInvoices = async (
    invoiceIds: number[],
    recalculatePenalties: boolean
  ): Promise<boolean> => {
    const response = await generateCancellationConcepts(invoiceIds, recalculatePenalties)
    if (response.success) {
      info(
        t(
          recalculatePenalties
            ? 'invoicesPage:notifications.success.recalculateInvoices'
            : 'invoicesPage:notifications.success.cancelInvoices'
        )
      )
    } else {
      error(
        t(
          recalculatePenalties
            ? 'invoicesPage:notifications.error.recalculateInvoices'
            : 'invoicesPage:notifications.error.cancelInvoices'
        )
      )
    }
    return response.success
  }

  const refreshInvoicesList = () =>
    fetchInvoicesList({
      commodity: props.commodity,
      clientType: props.clientType,
      activePageTab: props.activeTabInvoiceType,
      dateRange: props.dateRange
    })

  const handleAnnulateInvoices = async (selectedRows: InvoicesTableModel[]) => {
    if (!selectedRows || selectedRows.length === 0) {
      error(t('invoicesPage:notifications.error.noInvoicesSelected'))
      return
    }

    // Filter annullable invoices
    const filterResult = filterAnnullableInvoices(selectedRows)
    const { total, annullableRows, penaltyRows, annullableCount, nonAnnullableCount } = filterResult

    if (annullableCount === 0) {
      error(t('invoicesPage:notifications.error.noAnnullableInvoices'))
      return
    }

    // Show confirmation dialog
    const customMsg =
      nonAnnullableCount === 0
        ? t('confirmDialogs:ANNULATE_INVOICES.messageAll', { count: annullableCount })
        : t('confirmDialogs:ANNULATE_INVOICES.messagePartial', {
            count: nonAnnullableCount,
            annullable: annullableCount,
            total
          })

    await getConfirmation(ConfirmationDialogs.ANNULATE_INVOICES, customMsg).then(async () => {
      closeConfirmation()

      const penaltyInvoiceIds = penaltyRows.map(r => r.id)

      if (annullableRows.length === 0) {
        if (await processPenaltyInvoices(penaltyInvoiceIds, false)) await refreshInvoicesList()
        return
      }

      try {
        // First API call: get cycle list only (no execution data)
        const cyclesResponse = await getAnnulmentCycleStatus(
          props.activeTabInvoiceType ?? props.clientType ?? 0
        )

        if (!cyclesResponse.success || !cyclesResponse.data) {
          error(t('invoicesPage:notifications.error.fetchAnnulmentStatus'))
          return
        }

        const { invoiceCycleList } = cyclesResponse.data

        if (!invoiceCycleList || invoiceCycleList.length === 0) {
          error(t('invoicesPage:notifications.error.noCycleForSubjectType'))
          return
        }

        const invoiceIds = annullableRows.map(r => r.id)
        const minEmissionDate = moment.min(...annullableRows.map(r => moment(r.emisionDate)))
        const maxEmissionDate = moment.max(...annullableRows.map(r => moment(r.emisionDate)))

        // Open dialog for cycle selection; execution check happens on submit
        setPendingAnnulmentData({
          cycles: invoiceCycleList,
          minDate: minEmissionDate,
          maxDate: maxEmissionDate,
          invoiceIds,
          penaltyInvoiceIds
        })
        setShowAnnulmentCycleDialog(true)
      } catch (err) {
        error(t('invoicesPage:notifications.error.annulationError'))
        console.error('Error during annulation:', err)
      }
    })
  }

  /**
   * Handler unificado para anulación y refacturación.
   * Llama directamente al endpoint Reinvoice — sin verificación de estado de ejecución previa.
   */
  const handleCycleBatchFormSubmit = async (
    formData: ExecutionCycleFormDataModel,
    type: 'annulment' | 'reinvoice'
  ) => {
    const isAnnulment = type === 'annulment'
    const pendingData = isAnnulment ? pendingAnnulmentData : pendingReinvoiceData

    if (!pendingData || !formData.cycleSelect) return

    isAnnulment ? setIsSubmittingAnnulment(true) : setIsSubmittingReinvoice(true)
    isAnnulment ? setShowAnnulmentCycleDialog(false) : setShowReinvoiceCycleDialog(false)

    pendingCycleLabelRef.current = pendingData.cycles.find(
      (c: any) => c.invoiceCycleId === formData.cycleSelect
    )?.description
    pendingBillingTypeRef.current = isAnnulment
      ? EnumTipoFacturacion.Anuladora
      : EnumTipoFacturacion.Refacturacion
    try {
      const response = await createReinvoiceBatch({
        invoiceIds: pendingData.invoiceIds,
        cycleId: formData.cycleSelect,
        reinvoicingType: formData.reInvoiceType,
        groupingType: formData.groupingType
      })

      if (pendingData.penaltyInvoiceIds.length > 0) {
        await processPenaltyInvoices(pendingData.penaltyInvoiceIds, !isAnnulment)
      }

      if (response.success) {
        await refreshInvoicesList()

        // SignalR entregará executionCycleId vía EjecucionCFProcessing
        info(
          t(
            isAnnulment
              ? 'invoicesPage:notifications.success.annulationBatchCreated'
              : 'invoicesPage:notifications.success.reinvoiceBatchCreated'
          )
        )
      } else {
        if (pendingData.penaltyInvoiceIds.length > 0) await refreshInvoicesList()
        error(
          response.error?.message ??
            t(
              isAnnulment
                ? 'invoicesPage:notifications.error.annulationBatchError'
                : 'invoicesPage:notifications.error.reinvoiceBatchError'
            )
        )
      }
    } catch (err) {
      error(
        t(
          isAnnulment
            ? 'invoicesPage:notifications.error.annulationError'
            : 'invoicesPage:notifications.error.reinvoiceError'
        )
      )
      console.error('Error submitting cycle batch form:', err)
    } finally {
      isAnnulment ? setIsSubmittingAnnulment(false) : setIsSubmittingReinvoice(false)
      isAnnulment ? setPendingAnnulmentData(null) : setPendingReinvoiceData(null)
    }
  }

  // ─── Reinvoice (Refacturación) handlers ──────────────────────────────────────

  const handleReinvoiceInvoices = async (selectedRows: InvoicesTableModel[]) => {
    if (!selectedRows || selectedRows.length === 0) {
      error(t('invoicesPage:notifications.error.noInvoicesSelected'))
      return
    }

    const filterResult = filterReinvoiceableInvoices(selectedRows)
    const { total, reinvoiceableRows, penaltyRows, reinvoiceableCount, nonReinvoiceableCount } =
      filterResult

    if (reinvoiceableCount === 0) {
      error(t('invoicesPage:notifications.error.noReinvoiceableInvoices'))
      return
    }

    const customMsg =
      nonReinvoiceableCount === 0
        ? t('confirmDialogs:REINVOICE_INVOICES.messageAll', { count: reinvoiceableCount })
        : t('confirmDialogs:REINVOICE_INVOICES.messagePartial', {
            count: nonReinvoiceableCount,
            reinvoiceable: reinvoiceableCount,
            total
          })

    await getConfirmation(ConfirmationDialogs.REINVOICE_INVOICES, customMsg).then(async () => {
      closeConfirmation()

      const penaltyInvoiceIds = penaltyRows.map(r => r.id)

      if (reinvoiceableRows.length === 0) {
        if (await processPenaltyInvoices(penaltyInvoiceIds, true)) await refreshInvoicesList()
        return
      }

      try {
        const cyclesResponse = await getReinvoiceCycleStatus(
          props.activeTabInvoiceType ?? props.clientType ?? 0
        )

        if (!cyclesResponse.success || !cyclesResponse.data) {
          error(t('invoicesPage:notifications.error.fetchReinvoiceStatus'))
          return
        }

        const { invoiceCycleList } = cyclesResponse.data

        if (!invoiceCycleList || invoiceCycleList.length === 0) {
          error(t('invoicesPage:notifications.error.noCycleForReinvoice'))
          return
        }

        const invoiceIds = reinvoiceableRows.map(r => r.id)
        const minEmissionDate = moment.min(...reinvoiceableRows.map(r => moment(r.emisionDate)))
        const maxEmissionDate = moment.max(...reinvoiceableRows.map(r => moment(r.emisionDate)))

        setPendingReinvoiceData({
          cycles: invoiceCycleList,
          minDate: minEmissionDate,
          maxDate: maxEmissionDate,
          invoiceIds,
          penaltyInvoiceIds
        })
        setShowReinvoiceCycleDialog(true)
      } catch (err) {
        error(t('invoicesPage:notifications.error.reinvoiceError'))
        console.error('Error during reinvoice:', err)
      }
    })
  }

  const onClickRecalculateInvoice = async (row: InvoicesTableModel) => {
    if (isManualPenaltyInvoice(row)) {
      cancelInvoice(row, true)
    } else {
      handleReinvoiceInvoices([row])
    }
  }
  const onClickRecalculateInvoices = async (selectedRows: any[]) => {
    handleReinvoiceInvoices(selectedRows)
  }

  const onClickCancelInvoice = async (row: InvoicesTableModel) => {
    if (isManualPenaltyInvoice(row)) {
      cancelInvoice(row, false)
    } else {
      handleAnnulateInvoices([row])
    }
  }

  const onClickCancelInvoices = async (selectedRows: any[]) => {
    handleAnnulateInvoices(selectedRows)
  }

  //Determina si una fila puede ser anulada o refacturada
  const getRowActionFlags = (item: InvoicesTableModel) => ({
    canAnnul: isManualPenaltyInvoice(item) || isAnnullableInvoice(item),
    canReinvoice: isManualPenaltyInvoice(item) || isReinvoiceableInvoice(item)
  })

  const onDownloadMenuCloseHandler = () => {
    setDownloadMenuAnchorEl(null)
  }

  const onDownloadActionHandler = (row: InvoicesTableModel, event: any) => {
    if (row.url != null) {
      setDownloadMenuAnchorEl(event.currentTarget)
      setInvoiceRow(row)
    } else {
      error(t('invoicesPage:notifications.error.download'))
    }
  }

  const onClickObjectionsActionHandler = async (row: InvoicesTableModel) => {
    await getObjectionsByInvoiceID(row.id)
    setInvoiceRow(row)
    //setObjections(row.objections);
    setShowObjectionDialog(true)
  }

  const onClickManualInvoiceActionHandler = async (row: InvoicesTableModel) => {
    history.push(NonAuthRoutes.ManualInvoice, {
      InvoiceCod: row.code,
      invoiceId: row.id
    })
  }

  const onClickComplaintHandler = async (row: InvoicesTableModel) => {
    await fetchClientDataByCUPS(row.cups, row.startDate, row.endDate).then(response => {
      if (response.success) {
        const client = response.data as ClientFileModel
        setIsNavigating(true) // this state avoids triggering useEffect when changing Navigation
        history.push(NonAuthRoutes.ProceduresAdd, {
          invoiceId: row.id,
          formMode: ProcedureFormMode.RECLAMACION_FACTURA,
          clientData: client
        })
      } else {
        error(t('invoicesPage:notifications.error.fetchClient'))
      }
    })
  }

  const onDownloadMenuClickHandler = async (
    downloadMode: DownloadMode,
    isDistribution: boolean
  ) => {
    const downloadModeToFileExtension: Record<DownloadMode, EnumFileExtension> = {
      [DownloadMode.HTML]: EnumFileExtension.HTML,
      [DownloadMode.XML]: EnumFileExtension.XML,
      [DownloadMode.XSIG]: EnumFileExtension.XSIG,
      [DownloadMode.PDF]: EnumFileExtension.PDF,
      [DownloadMode.XLSX]: EnumFileExtension.XLSX
    }
    const blobFile = isDistribution
      ? {
          documentType: EnumFileType.InvoiceDocument,
          invoiceId: invoiceRow!.id,
          isDistribution: true
        }
      : {
          documentType: EnumFileType.InvoiceDocument,
          invoiceId: invoiceRow!.id,
          fileExtension: downloadModeToFileExtension[downloadMode]
        }
    const response = await apiDownloadFiles({ blobFiles: [blobFile] })
    const file = mapFileStreamToFile(response)
    if (file) fileDownloadHelper(file.name, file)
    onDownloadMenuCloseHandler()
  }

  // ----------- DELIVERY NOTES HANDLERS ----------- //

  const onClickShowDeliveryNotes = async (row: InvoicesTableModel) => {
    await fetchDeliveryNotes(row.id).then(response => {
      if (response.success) {
        setDeliveryNotesData(response.data)
        setShowDeliveryNotesDialog(true)
      } else {
        error(t('invoicesPage:notifications.error.deliveryNotes'))
      }
    })
  }

  const onCloseDeliveryNotesHandler = () => {
    setShowDeliveryNotesDialog(false)
  }

  // ----------- AGGREGATED INVOICE HANDLERS ----------- //

  const onClickShowAggregatedInvoice = async (row: InvoicesTableModel) => {
    await fetchInvoiceDetails(row.aggregatedInvoiceId!).then(response => {
      if (response.success) {
        response.data && setAggregatedInvoiceData([response.data])
        setShowAggregatedInvoiceDialog(true)
      } else {
        error(t('invoicesPage:notifications.error.aggregatedInvoice'))
      }
    })
  }

  const onCloseAggregatedInvoiceHandler = () => {
    setShowAggregatedInvoiceDialog(false)
  }

  // Handlers Ends

  // Functions Starts

  const fetchInvoicesList = async ({
    commodity,
    clientType,
    dateRange,
    activePageTab
  }: {
    commodity?: EnumCommodity
    clientType: EnumClientTypes
    dateRange: DatePeriod
    activePageTab?: TabsInvoicesType
  }) => {
    if (props.onFetching !== undefined) {
      props.onFetching(true)
    }

    setInvoices(undefined)

    await fetchInvoices({
      commodity,
      clientType,
      dateRange,
      invoiceCode: props.customFilter?.invoiceCode,
      subjectCode: props.customFilter?.subjectCode,
      contractCode: props.customFilter?.contractCode,
      cupsCode: props.customFilter?.cupsCode,
      activeDateFilter: props.activeDateFilter,
      filterDateBy: props.dateRange?.customFilter?.dateFilterType,
      invoiceStates: props.config?.queryInvoiceStates,
      activePageTab,
      subjectId: props.customFilter?.subjectId,
      hasObjections: props.customFilter?.hasObjections
    }).then(response => {
      if (props.onFetching !== undefined) {
        props.onFetching(false)
      }

      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          setInvoices(
            response.data?.map((item: InvoicesTableModel) => {
              return {
                ...completeDataTableActions(item),
                showComplaint: props.clientType === EnumClientTypes.DISTRIBUTOR ? true : false,
                isPenalizacion: isManualPenaltyInvoice(item),
                ...getRowActionFlags(item)
              }
            })
          )
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              // fetchInvoicesList(commodity, clientType, dateRange)
            }
          })
      }
    })
  }

  const completeDataTableActions = (item: InvoicesTableModel): InvoicesTableModel => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  const onSaveObjection = async (row: ObjectionTableModel, invoiceID: number) => {
    await saveObjection(row, invoiceID).then(response => {
      if (response.success) {
        getObjectionsByInvoiceID(invoiceID)
        let indexInvoice = invoices?.findIndex(x => x.id === invoiceID)
        let invoicesTmp = invoices?.filter(x => x.id !== invoiceID)
        invoiceRow!.hasObjections = true
        invoicesTmp?.splice(indexInvoice!, 0, invoiceRow!)

        setInvoices(
          invoicesTmp?.map((item: InvoicesTableModel) => {
            return completeDataTableActions(item)
          })
        )
      }
    })
  }

  const getObjectionsByInvoiceID = async (invoiceID: number) => {
    await fetchObjectionsByInvoiceID(invoiceID).then(res => {
      if (res.success) {
        setObjections(res.data)
      }
    })
  }

  const onRowDelete = async (row: ObjectionTableModel) => {
    getConfirmation(ConfirmationDialogs.DELETE_OBJECTION).then(async () => {
      await deleteObjection(row.id!).then(response => {
        if (response.success) {
          info(t('invoicesPage:objectionsTable.notifications.success.delete'))
          getObjectionsByInvoiceID(invoiceRow!.id)
        } else {
          error(t('invoicesPage:objectionsTable.notifications.error.delete'))
        }
      })
      closeConfirmation()
    })
  }
  // Functions Ends

  // DataTable Config Starts

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'id',
        display: false
      },
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML
      },
      {
        name: 'registrationDate',
        label: t('invoicesPage:table.columns.registrationDate'),
        type: DataType.DATE
      },
      {
        name: 'class',
        label: t('invoicesPage:table.columns.class'),
        type: DataType.TEXT
      },
      {
        name: 'code',
        label: t('invoicesPage:table.columns.code'),
        type: DataType.TEXT
      },
      {
        name: 'contractCode',
        label: t('invoicesPage:table.columns.contractCode'),
        type: tableConfiguration?.hideTableColumns ? DataType.LONGTEXT : DataType.TEXT,
        columnWidth: tableConfiguration?.hideTableColumns ? 80 : undefined,
        display: !tableConfiguration?.hideTableColumns?.includes('contractCode')
      },
      {
        name: 'subjectSource',
        label: t('invoicesPage:table.columns.clientSource'),
        type: DataType.TEXT,
        columnWidth: 100,
        display: !tableConfiguration?.hideTableColumns?.includes('subjectSource')
      },
      {
        name: 'subject',
        label: t('invoicesPage:table.columns.client'),
        type: DataType.TEXT,
        display: !tableConfiguration?.hideTableColumns?.includes('subject')
      },
      {
        name: 'cups',
        label: t('invoicesPage:table.columns.cups'),
        type: tableConfiguration?.hideTableColumns ? DataType.LONGTEXT : DataType.TEXT,
        columnWidth: tableConfiguration?.hideTableColumns ? 200 : undefined,
        display: !tableConfiguration.hideTableColumns?.includes('cups')
      },
      {
        name: 'startDate',
        label: t('invoicesPage:table.columns.startDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('invoicesPage:table.columns.endDate'),
        type: DataType.DATE
      },
      {
        name: 'emisionDate',
        label: t('invoicesPage:table.columns.emisionDate'),
        type: DataType.DATE
      },
      {
        name: 'invoiceType',
        label: t('invoicesPage:table.columns.invoiceType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoFactura, 'enums:EnumTipoFactura'),
        display: !tableConfiguration?.hideTableColumns?.includes('invoiceType')
      },
      {
        name: 'settlementGroupCode',
        label: t('invoicesPage:table.columns.settlementGroupCode'),
        type: DataType.TEXT,
        display: !tableConfiguration?.hideTableColumns?.includes('settlementGroupCode')
      },
      {
        name: 'amount',
        label: t('invoicesPage:table.columns.amount'),
        type: DataType.NUMBER
      },
      {
        name: 'status',
        label: t('invoicesPage:table.columns.status'),
        type: DataType.TEXT,
        display: !tableConfiguration?.hideTableColumns?.includes('status')
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfiguration, props.commodity, activeBalanceZone, activeCompany])

  const options = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      elevation: tableConfiguration.elevation,
      filterType: FilterType.ADVANCED,
      download: true,
      canEdit: false,
      canDelete: false,
      canInsert: false,
      pagination: true,
      rowsPerPage: dataTableRowsPerPage || tableConfiguration.rowsPerPage,
      rowsPerPageOptions: tableConfiguration.rowsPerPageOptions,
      selectableRows: true,
      customInsertEditModal: false,
      analytics: {
        customer: getCustomerDomain(),
        view: t('invoicesPage:table.analytics')
      },
      rowCustomActions: [
        {
          icon: <Block fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.cancelInvoice'),
          visibleIf: 'canAnnul',
          onClick: onClickCancelInvoice,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Autorenew fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.recalculateInvoice'),
          visibleIf: 'canReinvoice',
          onClick: onClickRecalculateInvoice,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <CloudDownloadIcon fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.downloadOk'),
          onClick: onDownloadActionHandler,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Warning fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.objections'),
          onClick: onClickObjectionsActionHandler,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.manualInvoiceDetail'),
          visibleIf: 'canManual',
          onClick: onClickManualInvoiceActionHandler,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <PriorityHigh fontSize="small" />,
          tooltip: t('procedures:components.genericDataTable.tooltips.complaint'),
          visibleIf: 'showComplaint',
          onClick: onClickComplaintHandler
        },
        {
          icon: <AllInbox fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.deliveryNotes'),
          visibleIf: 'showDeliveryNotes',
          onClick: onClickShowDeliveryNotes
        },
        {
          icon: <Assessment fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.aggregatedInvoice'),
          visibleIf: 'showAggregatedInvoice',
          onClick: onClickShowAggregatedInvoice
        }
      ],
      selectToolbarCustomActions: [
        {
          icon: <Block fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.cancelInvoices'),
          onClick: onClickCancelInvoices,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Autorenew fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.recalculateInvoice'),
          onClick: onClickRecalculateInvoices,
          userPermission: UserPermission.EDIT
        }
      ],
      onFilterChange: (filter: ColumnFilter[]) => {
        storeDataTableFilterForCurrentPage(filter)
      },
      onSearchChange: (search: string) => {
        storeDataTableSearchForCurrentPage(search)
      },
      onRowsPerPageChange: (rowsPerPage: number) => {
        storeDataTableRowsPerPageForCurrentPage(rowsPerPage)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [tableConfiguration, props.commodity, activeBalanceZone, activeCompany])

  const downloadOptions = React.useMemo(() => {
    if (invoiceRow?.urlDistribution == null) {
      return [
        {
          label: t('invoicesPage:table.customActions.html'),
          icon: <LoupeOutlined />,
          mode: DownloadMode.HTML,
          isDistribution: false
        },
        {
          label: t('invoicesPage:table.customActions.pdf'),
          icon: <ListOutlined />,
          mode: DownloadMode.PDF,
          isDistribution: false
        },
        {
          label: t('invoicesPage:table.customActions.xmlFacturae'),
          icon: <ListOutlined />,
          mode: DownloadMode.XML,
          isDistribution: false
        },
        {
          label: t('invoicesPage:table.customActions.xsig'),
          icon: <ListOutlined />,
          mode: DownloadMode.XSIG,
          isDistribution: false
        },
        {
          label: t('invoicesPage:table.customActions.xlsx'),
          icon: <ListOutlined />,
          mode: DownloadMode.XLSX,
          isDistribution: false
        }
      ]
    } else {
      return [
        {
          label: t('invoicesPage:table.customActions.xml'),
          icon: <ListOutlined />,
          mode: DownloadMode.XML,
          isDistribution: true
        },
        {
          label: t('invoicesPage:table.customActions.xmlFacturae'),
          icon: <ListOutlined />,
          mode: DownloadMode.XML,
          isDistribution: false
        }
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceRow])

  const dialogTableOptions = React.useMemo((): DataTableOptions => {
    return {
      download: true,
      canEdit: false,
      canDelete: false,
      canInsert: false,
      pagination: true,
      rowsPerPage: tableConfiguration.rowsPerPage,
      rowsPerPageOptions: tableConfiguration.rowsPerPageOptions,
      //selectableRows: true,
      customInsertEditModal: false,
      analytics: {
        customer: getCustomerDomain(),
        view: t('invoicesPage:table.analytics')
      },
      rowCustomActions: [
        {
          icon: <CloudDownloadIcon fontSize="small" />,
          tooltip: t('invoicesPage:table.tooltips.downloadOk'),
          onClick: onDownloadActionHandler,
          userPermission: UserPermission.EDIT
        }
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [])

  const dialogTableColumns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'id',
        display: false
      },
      {
        name: 'registrationDate',
        label: t('invoicesPage:table.columns.registrationDate'),
        type: DataType.DATE
      },
      {
        name: 'code',
        label: t('invoicesPage:table.columns.code'),
        type: DataType.TEXT
      },
      {
        name: 'contractCode',
        label: t('invoicesPage:table.columns.contractCode'),
        type: DataType.TEXT,
        display: !showAggregatedInvoiceDialog
      },
      {
        name: 'cups',
        label: t('invoicesPage:table.columns.cups'),
        type: DataType.TEXT,
        display: !showAggregatedInvoiceDialog
      },
      {
        name: 'startDate',
        label: t('invoicesPage:table.columns.startDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('invoicesPage:table.columns.endDate'),
        type: DataType.DATE
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAggregatedInvoiceDialog, activeBalanceZone, activeCompany])

  // DataTable Config Ends

  // Hooks Starts

  useEffect(() => {
    addEventListener('invoicesTable', ['EjecucionCFProcessing'], {
      onEventReceivedHandler: (event, date, data: EjecucionCFProcessingData) => {
        const cycleId = Number(data.ExecutionCycleId)
        if (!cycleId || cycleId <= 0) {
          console.warn(
            '[InvoicesTable] EjecucionCFProcessing: ExecutionCycleId inválido, ignorando',
            data
          )
          return
        }
        dispatch(
          addInvoiceCycleProcess(
            cycleId,
            EnumEstadoCiclo.Procesando,
            { billingType: pendingBillingTypeRef.current },
            pendingCycleLabelRef.current
          )
        )
      }
    })
    return () => deleteEventListener('invoicesTable')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isNavigating) return

    const getObjectionsDescriptions = async () => {
      await fetchObjectionsDescriptions().then(response => {
        setObjDescriptions(response.data)
      })
    }

    getObjectionsDescriptions()

    if (props.data !== undefined) {
      setInvoices(
        props.data.map((item: InvoicesTableModel) => ({
          ...completeDataTableActions(item),
          showComplaint: props.clientType === EnumClientTypes.DISTRIBUTOR ? true : false,
          isPenalizacion: isManualPenaltyInvoice(item),
          ...getRowActionFlags(item)
        }))
      )
      return
    }

    fetchInvoicesList({
      commodity: props.commodity,
      clientType: props.clientType,
      activePageTab: props.activeTabInvoiceType,
      dateRange: props.dateRange
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeBalanceZone,
    props.commodity,
    props.clientType,
    props.activeTabInvoiceType,
    props.dateRange,
    props.data
  ])

  // Hooks Ends

  return (
    <>
      <DataTable
        data={invoices}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.InvoicesPage)}
        appliedFilter={props.dataTableFilter}
        appliedSearch={props.dataTableSearch}
      />

      <ObjectionsDialog
        open={showObjectionDialog}
        handleClose={() => setShowObjectionDialog(false)}
        title={t('invoicesPage:dialogs.objections.title')}
        objDescriptions={objDescriptions ?? []}
        objectionsData={objections}
        tableUserPermissions={getUserPermissions(Pages.InvoicesPage)}
        errorProps={errorProps}
        hasEditPermission={hasPermission(Resources.InvoicesPage, [UserPermission.EDIT])}
        allowInsert
        onRowAdd={row => onSaveObjection(row, invoiceRow!.id)}
        onRowDelete={onRowDelete}
      />

      <Menu
        keepMounted={false}
        elevation={2}
        anchorEl={downloadMenuAnchorEl}
        open={Boolean(downloadMenuAnchorEl)}
        onClose={onDownloadMenuCloseHandler}
      >
        {downloadOptions.map((downloadOption, index) => (
          <MenuItem
            dense
            key={index}
            onClick={() =>
              onDownloadMenuClickHandler(downloadOption.mode, downloadOption.isDistribution)
            }
          >
            <Typography variant="inherit">{downloadOption.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <SimpleDialog
        open={showDeliveryNotesDialog}
        handleClose={onCloseDeliveryNotesHandler}
        title={t('invoicesPage:dialogs.deliveryNotes.title')}
      >
        <DataTable
          data={deliveryNotesData}
          columns={dialogTableColumns}
          options={dialogTableOptions}
          info={DataTableErrorHandler(errorProps)}
          userPermissions={getUserPermissions(Pages.InvoicesPage)}
        />
      </SimpleDialog>

      <SimpleDialog
        open={showAggregatedInvoiceDialog}
        handleClose={onCloseAggregatedInvoiceHandler}
        title={t('invoicesPage:dialogs.aggregatedInvoice.title')}
      >
        {aggregatedInvoiceData && (
          <DataTable
            data={aggregatedInvoiceData}
            columns={dialogTableColumns}
            options={dialogTableOptions}
            info={DataTableErrorHandler(errorProps)}
            userPermissions={getUserPermissions(Pages.InvoicesPage)}
          />
        )}
      </SimpleDialog>

      {/* Annulment Cycle Selection Dialog */}
      {pendingAnnulmentData && (
        <ExecutionCycleFormDialog
          open={showAnnulmentCycleDialog}
          onClose={formData => {
            if (formData) {
              handleCycleBatchFormSubmit(formData, 'annulment')
            } else {
              setShowAnnulmentCycleDialog(false)
              setPendingAnnulmentData(null)
            }
          }}
          mode="start"
          hideDates={true}
          footerNote={
            pendingAnnulmentData.penaltyInvoiceIds.length > 0
              ? t('invoicesPage:dialogs.penaltyCycleNote.annulment')
              : undefined
          }
          availableCycles={pendingAnnulmentData.cycles.map(cycle => ({
            value: cycle.invoiceCycleId,
            label: cycle.description,
            invoiceType: cycle.invoiceType,
            invoiceCycleSubtype: cycle.invoiceCycleType ?? undefined
          }))}
          initialData={{
            startDate: pendingAnnulmentData.minDate.format('YYYY-MM-DD'),
            endDate: pendingAnnulmentData.maxDate.format('YYYY-MM-DD'),
            // Pre-select cycle if only one
            ...(pendingAnnulmentData.cycles.length === 1 && {
              cycleSelect: pendingAnnulmentData.cycles[0].invoiceCycleId
            })
          }}
        />
      )}

      {/* Reinvoice Cycle Selection Dialog */}
      {pendingReinvoiceData && (
        <ExecutionCycleFormDialog
          open={showReinvoiceCycleDialog}
          onClose={formData => {
            if (formData) {
              handleCycleBatchFormSubmit(formData, 'reinvoice')
            } else {
              setShowReinvoiceCycleDialog(false)
              setPendingReinvoiceData(null)
            }
          }}
          mode="start"
          hideDates={true}
          footerNote={
            pendingReinvoiceData.penaltyInvoiceIds.length > 0
              ? t('invoicesPage:dialogs.penaltyCycleNote.reinvoice')
              : undefined
          }
          availableCycles={pendingReinvoiceData.cycles.map(cycle => ({
            value: cycle.invoiceCycleId,
            label: cycle.description,
            invoiceType: cycle.invoiceType,
            invoiceCycleSubtype: cycle.invoiceCycleType ?? undefined
          }))}
          initialData={{
            startDate: pendingReinvoiceData.minDate.format('YYYY-MM-DD'),
            endDate: pendingReinvoiceData.maxDate.format('YYYY-MM-DD'),
            ...(pendingReinvoiceData.cycles.length === 1 && {
              cycleSelect: pendingReinvoiceData.cycles[0].invoiceCycleId
            })
          }}
        />
      )}
    </>
  )
}
